//changed due to package upgrade
import React, { useState } from 'react';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import PropTypes from 'prop-types';
import { Box, Button, Grid, Typography, alpha } from '@mui/material';
import jwtAxios from '@projectK/services/auth/JWT';
import AppScrollbar from '@projectK/components/AppScrollbar';
import { useIntl } from 'react-intl';
import AppLoader from '@projectK/components/AppLoader';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import AppCard from '@projectK/components/AppCard';
import { useInfoViewActionsContext } from '@projectK/context/InfoViewContextProvider';
import AppInfoView from '@projectK/components/AppInfoView';
import { useTheme } from '@mui/system';
import useMediaQuery from '@mui/material/useMediaQuery';
import AppsHeader from '@projectK/components/AppsHeader';
import '../../../../assets/styles/activities.css';
import { useMobileSettings } from '@projectK/context/MobileContextProvider';
import {
  ActivitySessionsMain,
  ButtonMain,
  SearchFieldMain,
} from '@projectK/MainComponents';
import { useToast } from '@projectK/context/ToastProvider';
import { isParent } from '@projectK/context/RolesProvider';
import RestrictedPopupDialog from '@projectK/modules/apps/RestrictedPopupDialog';

const ActivitySessions = ({
  list,
  setList,
  loading,
  setLoading,
  setDateFilter,
  setSearchQuery,
  setShowAll,
  showAll,
  useDateFilter,
  fetchActivitySessions,
  fetchMySchedule,
  fetchWaitList,
  fetchUserProduct,
}) => {
  const { isMobile, mobileMode } = useMobileSettings();
  const companyTheme = CompanyTheme();
  const mobileResponse = useMediaQuery('(max-width:755px)');
  const showToast = useToast();
  const theme = useTheme();
  const { messages } = useIntl();
  const { user } = useAuthUser();
  const infoViewActionsContext = useInfoViewActionsContext();
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);

  const onSearchActivity = (e) => {
    setSearchQuery(e.target.value);
    setList([]);
    setLoading(true);
  };
  const toggleShowAll = () => {
    setShowAll(!showAll);
    setDateFilter(!useDateFilter);
  };

  const handleSignUp = async (user_id, id, product_id) => {
    try {
      setLoading(true);
      // Make the POST request using jwtAxios
      await jwtAxios.post(`/user-session`, {
        user_id: user_id,
        activity_session_id: id,
        product_id: product_id,
      });
      fetchActivitySessions();
      fetchMySchedule();
      if (!isMobile) {
        fetchUserProduct();
        infoViewActionsContext.showMessage(
          `Signed up to session successfully.`,
        );
      } else {
        showToast('Signed up to session successfully.', 'success', mobileMode);
      }
      const isParentUser = isParent(user);
      jwtAxios.get(
        `/events/update/user/calender`,
        isParentUser
          ? {
              params: {
                user_id: user_id,
              },
            }
          : {},
      );
      setLoading(false);
    } catch (error) {
      setLoading(false);
      if (error.status === 423) {
        setIsErrorDialogOpen(true);
      } else if (isMobile) {
        showToast(error?.response?.data?.error, 'error', mobileMode);
      } else {
        infoViewActionsContext.fetchError(error?.response?.data?.error);
      }
    }
  };


  const handleWaitlist = async (user_id, id, product_id) => {
    try {
      setLoading(true);
      // Make the POST request using jwtAxios
      await jwtAxios.post(`/user-waitlist`, {
        user_id: user_id,
        activity_session_id: id,
        product_id: product_id,
      });
      setLoading(false);
      fetchActivitySessions();
      fetchMySchedule();
      if (!isMobile) {
        fetchUserProduct();
        infoViewActionsContext.showMessage(
          `Session added to waitlist successfully.`,
        );
      } else {
        showToast('Signed up to waitlist successfully.', 'success', mobileMode);
      }
      fetchWaitList();
    } catch (error) {
      setLoading(false);
      if (error.status === 423) {
        setIsErrorDialogOpen(true);
      } else if (isMobile) {
        showToast(error?.response?.data?.error, 'error', mobileMode);
      } else {
        infoViewActionsContext.fetchError(error?.response?.data?.error);
      }
    }
  };
  function getTruncatedAddressTitle(data) {
    const address = data?.address ?? '';
    const locationName = data?.location_name ?? '';
    return `${address}${locationName}`.substring(0, 50);
  }

  const handleConditionFunction = (condition, ifData, elseData) => {
    if (condition) {
      return ifData;
    } else {
      return elseData;
    }
  };

  const responsiveSize = { xs: 12, md: 6, xl: 4 };
  const activity = (
    <>
      {loading && <AppLoader />}
      {list && (
        <>
          {''}
          {list.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                padding: '20px',
              }}
            >
              <CalendarMonthIcon sx={{ fontSize: 40, marginBottom: 6 }} />
              <Typography
                variant='body1'
                sx={{ fontWeight: 'bold', fontSize: 18 }}
              >
                 No upcoming schedules
              </Typography>
            </Box>
          ) : (
            <Grid
              container
              spacing={2}
              className={!isMobile ? 'activity-session' : ''}
            >
              {list.map((data, index) => {
                const truncatedAddressTitle = getTruncatedAddressTitle(data);
                const truncatedResourceTitle = data?.resource_name
                  ? data.resource_name.substring(0, 50)
                  : null;
                return (
                  <Grid  key={data?.id} size={{...responsiveSize}}>
                    <ActivitySessionsMain
                      data={data}
                      mobileMode={mobileMode}
                      handleSignUp={handleSignUp}
                      handleWaitlist={handleWaitlist}
                      companyTheme={companyTheme}
                      truncatedAddressTitle={truncatedAddressTitle}
                      truncatedResourceTitle={truncatedResourceTitle}
                      user={user}
                      theme={theme}
                      index={index}
                      loading={loading}
                    />
                  </Grid>
                );
              })}
            </Grid>
          )}
        </>
      )}
    </>
  );
  return (
    <AppCard>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography
          variant='h4'
          sx={{
            color: handleConditionFunction(
              companyTheme,
              companyTheme?.primary?.main,
              'text.primary',
            ),
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '10px',
            paddingLeft: '10px',
          }}
        >
          {messages['common.upcommingActivities']}
        </Typography>

        {isMobile && (
          <ButtonMain
            color='primary'
            fill='outline'
            size={handleConditionFunction(mobileResponse, 'small', 'medium')}
            style={{
              borderColor: handleConditionFunction(
                companyTheme,
                companyTheme?.primary?.main,
                '#2196f3',
              ),
              color: handleConditionFunction(
                companyTheme,
                companyTheme?.primary?.main,
                'primary',
              ),
              marginLeft: '10px',
            }}
            sx={{
              borderColor: handleConditionFunction(
                companyTheme,
                companyTheme?.primary?.main,
                '#2196f3',
              ),
              '&:hover': {
                borderColor: companyTheme
                  ? alpha(companyTheme.primary.main, 0.8)
                  : alpha('#2196f3', 0.8),
              },
              color: handleConditionFunction(
                companyTheme,
                companyTheme?.primary?.main,
                'primary',
              ),
            }}
            onClick={toggleShowAll}
            variant='outlined'
            name={handleConditionFunction(showAll, 'View All', 'View Month')}
          />
        )}
      </div>
      <AppsHeader>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            width: 1,
          }}
        >
          <SearchFieldMain
            // iconPosition='right'
            overlap={false}
            onChange={onSearchActivity}
            placeholder={messages['common.searchHere']}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              ml: 'auto',
            }}
          >
            <Button
              color='primary'
              size={handleConditionFunction(mobileResponse, 'small', 'medium')}
              sx={{
                borderColor: handleConditionFunction(
                  companyTheme,
                  companyTheme?.primary?.main,
                  '#2196f3',
                ),
                '&:hover': {
                  borderColor: companyTheme
                    ? alpha(companyTheme.primary.main, 0.8)
                    : alpha('#2196f3', 0.8),
                },
                color: handleConditionFunction(
                  companyTheme,
                  companyTheme?.primary?.main,
                  'primary',
                ),
                display: isMobile && 'none',
              }}
              onClick={toggleShowAll}
              variant='outlined'
            >
              {handleConditionFunction(showAll, 'View All', 'View Month')}
            </Button>
          </Box>
        </Box>
      </AppsHeader>
      {isMobile ? (
        activity
      ) : (
        <AppScrollbar className='scroll-products'>{activity}</AppScrollbar>
      )}
      <AppInfoView />
      {isErrorDialogOpen && (
        <RestrictedPopupDialog
          setIsErrorDialogOpen={setIsErrorDialogOpen}
          isErrorDialogOpen={isErrorDialogOpen}
        />
      )}
    </AppCard>
  );
};

export default ActivitySessions;

ActivitySessions.propTypes = {
  list: PropTypes.array,
  setList: PropTypes.func,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  setDateFilter: PropTypes.func,
  setSearchQuery: PropTypes.func,
  setShowAll: PropTypes.func,
  showAll: PropTypes.bool,
  useDateFilter: PropTypes.bool,
  fetchActivitySessions: PropTypes.func,
  fetchMySchedule: PropTypes.func,
  fetchWaitList: PropTypes.func,
  fetchUserProduct: PropTypes.func,
};
